import { getMyIpMediaResourceKey, getMyIpMediaUrl } from './mediaUrl';
import type {
  MyIpDraftSnapshot, MyIpItem, MyIpProfile, MyIpResource,
} from './types';

export const MY_IP_RESOURCE_TYPES: Record<string, string> = {
  additional_image: 'image', three_view: 'image', voice: 'audio', video: 'video',
};

/** 正式关系与待保存直链使用同一媒体键，签名刷新不新增槽位。 */
export function getMyIpResourceKey(resource: MyIpResource): string {
  return getMyIpMediaResourceKey(resource.url || resource.assetId) || resource.assetId;
}

/** 删除、禁用的关系不再投影为可用媒体。 */
export function isMyIpResourceActive(resource: MyIpResource): boolean {
  return !['deleted', 'removed', 'disabled'].includes(resource.status);
}

/** 单槽替换复用真实关系 ID；附图按 URL/资产身份去重，不按数组位置覆盖。 */
export function upsertMyIpResource(resources: MyIpResource[], media: Partial<MyIpResource> & Pick<MyIpResource, 'role' | 'type'>): MyIpResource[] {
  if (MY_IP_RESOURCE_TYPES[media.role] !== media.type) throw new Error('Invalid IP resource slot type');
  const candidate: MyIpResource = {
    id: '', assetId: '', sortOrder: 0, status: 'active', ...media,
  };
  const existing = resources.find((entry) => isMyIpResourceActive(entry) && entry.role === candidate.role
    && ((candidate.id && entry.id === candidate.id) || candidate.role !== 'additional_image' || getMyIpResourceKey(entry) === getMyIpResourceKey(candidate)));
  const next = {
    ...existing,
    ...candidate,
    ...(media.url && media.url !== existing?.url ? { available: undefined } : {}),
    id: existing?.id || '',
    sortOrder: media.sortOrder ?? existing?.sortOrder ?? resources.filter((entry) => entry.role === candidate.role).length,
    // 新音频没有音色身份时清空旧值；同一素材的签名刷新仍保留身份。
    ...(existing?.voiceId && media.voiceId === undefined && candidate.role === 'voice'
      && getMyIpResourceKey(existing) !== getMyIpResourceKey(candidate) ? { voiceId: '' } : {}),
  };
  return existing ? resources.map((entry) => (entry === existing ? next : entry)) : [...resources, next];
}

/** 已下线的 profile.materials 不再进入客户端主档或任何写请求。 */
export function omitMyIpProfileMaterials(value: MyIpProfile): MyIpProfile {
  const { materials: ignoredMaterials, ...profile } = value;
  return profile;
}

/** 剔除已下线的 materials，其他存量媒体键不新增内容；媒体统一写 resources。 */
export function preserveMyIpLegacyMedia(base: MyIpProfile, next: MyIpProfile): MyIpProfile {
  const profile = omitMyIpProfileMaterials(next);
  for (const key of ['main_visual', 'turnaround_visual', 'voice', 'voice_selection']) {
    if (Object.hasOwn(base, key)) profile[key] = base[key];
    else delete profile[key];
  }
  const sync = profile.material_sync;
  if (sync && typeof sync === 'object' && !Array.isArray(sync)) {
    // 只保留删除意图，不能把新的媒体集合藏到 profile 的同步元数据中。
    const previous = base.material_sync as Record<string, unknown> | undefined;
    profile.material_sync = { ...sync, additions: previous?.additions || [] };
  }
  return profile;
}

/** Agent 的媒体描述转换为槽位；描述中的素材记录 id 从不当作关系 id。 */
export function materialToMyIpResource(value: unknown): Partial<MyIpResource> & Pick<MyIpResource, 'role' | 'type'> | undefined {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const entry = value as Record<string, unknown>;
  const asset = entry.asset && typeof entry.asset === 'object' ? entry.asset as Record<string, unknown> : entry;
  const type = asset.resource_type || asset.type || asset.media_type || MY_IP_RESOURCE_TYPES[String(entry.resource_role)];
  const role = typeof entry.resource_role === 'string' && MY_IP_RESOURCE_TYPES[entry.resource_role]
    ? entry.resource_role : ({ image: 'additional_image', video: 'video', audio: 'voice' } as Record<string, string>)[String(type)];
  const url = getMyIpMediaUrl(asset.url || asset.media_url || asset.ori_media_url);
  const rawAssetId = typeof asset.asset_id === 'number' && Number.isSafeInteger(asset.asset_id) ? String(asset.asset_id) : asset.asset_id;
  const assetId = typeof rawAssetId === 'string' ? rawAssetId : '';
  if (!role || (!url && !assetId)) return undefined;
  return {
    role,
    type: String(type),
    assetId: getMyIpMediaUrl(assetId) ? '' : assetId,
    url: url || getMyIpMediaUrl(assetId),
    ...(typeof asset.voice_id === 'string' || (typeof asset.voice_id === 'number' && Number.isSafeInteger(asset.voice_id)) ? { voiceId: String(asset.voice_id) } : {}),
    ...(typeof (asset.name || asset.desc) === 'string' ? { name: String(asset.name || asset.desc) } : {}),
    ...(typeof asset.format === 'string' ? { fileExtension: asset.format } : {}),
    ...(asset.thumbnail_url !== undefined || asset.media_cover_url !== undefined ? { thumbnailUrl: getMyIpMediaUrl(asset.thumbnail_url || asset.media_cover_url) } : {}),
    ...(asset.watermark_url !== undefined || asset.media_url_by_water_mark !== undefined
      ? { watermarkUrl: getMyIpMediaUrl(asset.watermark_url || asset.media_url_by_water_mark) || '' } : {}),
    ...(asset.watermark_cover_url !== undefined || asset.media_cover_url_by_water_mark !== undefined
      ? { watermarkCoverUrl: getMyIpMediaUrl(asset.watermark_cover_url || asset.media_cover_url_by_water_mark) || '' } : {}),
    ...(typeof asset.duration === 'number' ? { duration: asset.duration } : {}),
    ...(typeof asset.width === 'number' ? { width: asset.width } : {}),
    ...(typeof asset.height === 'number' ? { height: asset.height } : {}),
    ...(typeof entry.sort_order === 'number' ? { sortOrder: entry.sort_order } : {}),
  };
}

/** 将旧摘要格式在客户端边界转换为资源快照，不再把摘要媒体写回 profile。 */
export function getMyIpResourceSnapshot(item: MyIpItem, snapshot: MyIpDraftSnapshot): MyIpDraftSnapshot {
  let resources = snapshot.resources || item.resources;
  const { profile } = snapshot;
  if (profile.materials !== item.profile.materials && Array.isArray(profile.materials)) {
    for (const value of profile.materials) {
      const media = materialToMyIpResource(value);
      if (media) resources = upsertMyIpResource(resources, media);
    }
  }
  const threeView = getMyIpMediaUrl(profile.turnaround_visual);
  if (threeView) {
    resources = upsertMyIpResource(resources, { role: 'three_view', type: 'image', url: threeView });
  }
  const primary = [snapshot.coverAssetId, threeView].map(getMyIpMediaResourceKey).filter(Boolean);
  resources = resources.filter((resource) => resource.role !== 'additional_image' || !primary.includes(getMyIpResourceKey(resource)));
  return { ...snapshot, profile: preserveMyIpLegacyMedia(item.profile, profile), resources };
}

/** 冲突只重放本地相对基线的槽位变化，保留其他客户端新加的资源。 */
export function rebaseMyIpResources(base: MyIpResource[], local: MyIpResource[], latest: MyIpResource[]): MyIpResource[] {
  let merged = [...latest];
  for (const previous of base) {
    // 入队基线可能还没有关系 ID，后续快照已经收到服务端 ID 并换过 URL。
    const resolvedId = previous.id || latest.find((entry) => entry.role === previous.role
      && getMyIpResourceKey(entry) === getMyIpResourceKey(previous))?.id;
    const next = local.find((entry) => (resolvedId && entry.id === resolvedId)
      || (entry.role === previous.role && getMyIpResourceKey(entry) === getMyIpResourceKey(previous)));
    if (!next) { merged = merged.filter((entry) => !(previous.id && entry.id === previous.id)
      && !(entry.role === previous.role && getMyIpResourceKey(entry) === getMyIpResourceKey(previous))); }
  }
  for (const resource of local) {
    const previous = base.find((entry) => (resource.id && entry.id === resource.id)
      || (entry.role === resource.role && getMyIpResourceKey(entry) === getMyIpResourceKey(resource)));
    if (!previous || JSON.stringify(previous) !== JSON.stringify(resource)) merged = upsertMyIpResource(merged, resource);
  }
  return merged;
}
